/**
 * Smart Pricing utilities.
 *
 * Suggest optimal pricing based on:
 *   - Season/time of year
 *   - Day of week
 *   - Local demand signals (occupancy rate)
 *   - Competition analysis (via similar listings)
 *
 * Config in listing.publicData.smartPricing = {
 *   enabled: boolean,
 *   baselineCents: number,
 *   minCents: number,
 *   maxCents: number,
 *   weekendMultiplier: number,   — default 1.2
 *   peakSeasonMultiplier: number — default 1.3
 * }
 *
 * Peak seasons configured per locale.
 */

export const DEFAULT_WEEKEND_MULTIPLIER = 1.2;
export const DEFAULT_PEAK_MULTIPLIER = 1.3;

// Peak months (1-indexed): June, July, August, December
export const PEAK_MONTHS = [6, 7, 8, 12];

/**
 * Check if a date falls in peak season.
 * @param {Date} date
 * @param {number[]} [peakMonths]
 * @returns {boolean}
 */
export const isPeakSeason = (date, peakMonths = PEAK_MONTHS) =>
  peakMonths.includes(date.getMonth() + 1);

/**
 * Check if a date is a weekend (Sat/Sun).
 * @param {Date} date
 * @returns {boolean}
 */
export const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

/**
 * Calculate smart price suggestion for a date.
 * @param {number} baselineCents
 * @param {Date} date
 * @param {Object} config — { minCents, maxCents, weekendMultiplier, peakSeasonMultiplier }
 * @returns {{ suggestedCents: number, isWeekend: boolean, isPeak: boolean }}
 */
export const suggestPrice = (baselineCents, date, config = {}) => {
  let price = baselineCents;
  const weekend = isWeekend(date);
  const peak = isPeakSeason(date, config.peakMonths);

  if (weekend) price = Math.round(price * (config.weekendMultiplier || DEFAULT_WEEKEND_MULTIPLIER));
  if (peak) price = Math.round(price * (config.peakSeasonMultiplier || DEFAULT_PEAK_MULTIPLIER));

  if (config.minCents && price < config.minCents) price = config.minCents;
  if (config.maxCents && price > config.maxCents) price = config.maxCents;

  return { suggestedCents: price, isWeekend: weekend, isPeak: peak };
};

/**
 * Calculate average suggested price over a date range.
 * @param {number} baselineCents
 * @param {Date} start
 * @param {Date} end
 * @param {Object} config
 * @returns {number} — average suggested price in cents
 */
export const averageSuggestedPrice = (baselineCents, start, end, config = {}) => {
  const dates = [];
  let current = new Date(start);
  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  if (dates.length === 0) return baselineCents;
  const total = dates.reduce((sum, d) => sum + suggestPrice(baselineCents, d, config).suggestedCents, 0);
  return Math.round(total / dates.length);
};
